import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSION_GROUPS, PermissionInfo, Role } from '@cloudsign/shared';
import type { SiteScope } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan, useCaps } from '../../lib/session';
import { useToastOnSuccess } from '../../components/Toast';
import { Alert, Badge, Button, Card, CheckboxField, ConfirmButton, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';

const PermissionList = z.array(PermissionInfo);
type PermInfo = z.infer<typeof PermissionInfo>;

export function RoleEditPage() {
  const { id = 'new' } = useParams();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const qc = useQueryClient();
  const caps = useCaps();
  const canManage = useCan('roles.manage');

  const role = useQuery({ queryKey: ['roles', id], queryFn: () => api.get(`/roles/${id}`, { schema: Role }), enabled: !isNew });
  const catalogue = useQuery({ queryKey: ['permissions'], queryFn: () => api.get('/permissions', { schema: PermissionList }) });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [siteScope, setSiteScope] = useState<SiteScope>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (role.data) {
      setName(role.data.name);
      setDescription(role.data.description);
      setSiteScope(role.data.siteScope);
      setSelected(new Set(role.data.permissions));
    }
  }, [role.data]);

  const mine = useMemo(() => new Set(caps.permissions), [caps.permissions]);
  const readOnly = !canManage || (!isNew && role.data ? !role.data.editable : false);

  const save = useMutation({
    mutationFn: () => {
      const body = { name, description, siteScope, permissions: [...selected] };
      return isNew ? api.post('/roles', { body, schema: Role }) : api.patch(`/roles/${id}`, { body, schema: Role });
    },
    onSuccess: async (r) => {
      await qc.invalidateQueries({ queryKey: ['roles'] });
      if (isNew) void navigate(`/admin/roles/${r.id}`, { replace: true });
    },
  });
  useToastOnSuccess(save.isSuccess, t('common.saved'));
  const remove = useMutation({
    mutationFn: () => api.delete(`/roles/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['roles'] });
      void navigate('/admin/roles', { replace: true });
    },
  });

  const groups = useMemo(() => {
    const by = new Map<string, PermInfo[]>();
    for (const p of catalogue.data ?? []) by.set(p.group, [...(by.get(p.group) ?? []), p]);
    return PERMISSION_GROUPS.filter((g) => by.has(g)).map((g) => ({ group: g, perms: by.get(g) ?? [] }));
  }, [catalogue.data]);

  if (!isNew && role.isPending) return <Spinner />;
  if (!isNew && role.isError) return <ErrorPanel error={role.error} onRetry={() => void role.refetch()} />;
  if (catalogue.isPending) return <Spinner />;
  if (catalogue.isError) return <ErrorPanel error={catalogue.error} onRetry={() => void catalogue.refetch()} />;

  const errs = fieldErrors(save.error);
  const toggle = (key: string, on: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });
  const toggleGroup = (perms: PermInfo[], on: boolean) =>
    setSelected((s) => {
      const next = new Set(s);
      for (const p of perms) {
        if (!mine.has(p.key)) continue;
        if (on) next.add(p.key);
        else next.delete(p.key);
      }
      return next;
    });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  const title = isNew ? t('roles.create') : role.data?.key ? t(`role.${role.data.key}`) : name;

  return (
    <>
      <PageHeader title={title} actions={<Link className="text-brand hover:underline" to="/admin/roles">{t('common.back')}</Link>} />
      <form onSubmit={submit} className="space-y-6">
        <ErrorLine error={save.error ?? remove.error} />
                {!isNew && role.data && !role.data.editable ? <Alert kind="info">{t('roles.builtinLocked')}</Alert> : null}
        <Card title={t('roles.details')}>
          <div className="grid max-w-2xl gap-4">
            <TextField label={t('common.name')} required maxLength={80} value={name} disabled={readOnly} onChange={(e) => setName(e.target.value)} error={errs['name']} />
            <TextField label={t('roles.description')} maxLength={500} value={description} disabled={readOnly} onChange={(e) => setDescription(e.target.value)} />
            <SelectField label={t('roles.siteScope')} value={siteScope} disabled={readOnly} onChange={(e) => setSiteScope(e.target.value === 'assigned' ? 'assigned' : 'all')}>
              <option value="all">{t('role.siteScope.all')}</option>
              <option value="assigned">{t('role.siteScope.assigned')}</option>
            </SelectField>
          </div>
        </Card>

        <Card title={t('roles.permissions')}>
          <p className="mb-4 text-sm text-slate-600">{t('roles.permissionsHelp')}</p>
          <div className="space-y-6">
            {groups.map(({ group, perms }) => {
              const grantable = perms.filter((p) => mine.has(p.key));
              const allOn = grantable.length > 0 && grantable.every((p) => selected.has(p.key));
              return (
                <fieldset key={group} className="rounded-md border border-slate-300 p-3">
                  <legend className="px-1 font-semibold">{t(`permGroup.${group}`)}</legend>
                  {!readOnly && grantable.length > 0 ? (
                    <Button className="mb-2" onClick={() => toggleGroup(perms, !allOn)}>
                      {allOn ? t('roles.clearGroup') : t('roles.selectGroup')}
                    </Button>
                  ) : null}
                  <div className="grid gap-2 sm:grid-cols-2">
                    {perms.map((p) => {
                      const lacking = !mine.has(p.key);
                      return (
                        <div key={p.key}>
                          <CheckboxField
                            label={
                              <>
                                {t(`perm.${p.key}`)}{' '}
                                {p.sensitive ? <Badge tone="warn">{t('roles.sensitive')}</Badge> : null}
                                {!p.moduleEnabled ? <Badge>{t('roles.moduleOff')}</Badge> : null}
                              </>
                            }
                            hint={lacking && !readOnly ? t('roles.cannotGrant') : undefined}
                            checked={selected.has(p.key)}
                            disabled={readOnly || lacking}
                            onChange={(e) => toggle(p.key, e.target.checked)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </fieldset>
              );
            })}
          </div>
        </Card>

        {!readOnly ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button type="submit" variant="primary" disabled={save.isPending}>
              {t('common.save')}
            </Button>
            {!isNew && role.data && !role.data.builtin ? (
              <ConfirmButton label={t('roles.delete')} pending={remove.isPending} onConfirm={() => remove.mutate()} />
            ) : null}
          </div>
        ) : null}
      </form>
    </>
  );
}
